/**
 * docxReport.js — редактируемый бланк отчёта в формате DOCX. [11.1.19]
 *
 * ПОВОД — предложение владельца: «переделать все отчёты в docx, тогда вопрос
 * [кириллицы] закроется». Кириллицу в PDF мы починили в корне (jsPDF со
 * шрифтами без кириллицы изгнан, PDF-движок один — Chromium), но у DOCX есть
 * СВОЯ ценность, которой у PDF нет: врач ОТКРЫВАЕТ бланк в Word и ДОПИСЫВАЕТ
 * заключение. Поэтому DOCX добавлен третьей кнопкой рядом с «Печатью» и
 * «PDF», а не вместо них: PDF — формат обмена и печати, DOCX — правки.
 * Кириллица здесь верна ПО ПОСТРОЕНИЮ: document.xml — обычный UTF-8.
 *
 * УСТРОЙСТВО. DOCX — это zip из XML-частей. Сборщики частей — ЧИСТЫЕ функции
 * (проверяются verify_11_1_19 без DOM и без распаковки); в архив их кладёт
 * штатный fflate (тот же, что жмёт ZIP исследований) — асинхронно, по
 * доктрине studyArchiveModule «всё 100% Async». Единственный статический
 * импорт — downloadBlob.
 *
 * Данные бланка приходят из featureBindings.собратьДанныеОтчёта() — ТЕ ЖЕ,
 * что у HTML-бланка Печати/PDF: три формы не разъедутся по построению.
 */

import { downloadBlob } from '../utils/helpers.js';

const экр = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// [11.1.30] Шрифт документа задаётся врачом в настройках бланка (просьба
// владельца). Держится модульной переменной: собиратели абзацев зовутся из
// десятка мест, и таскать шрифт параметром через все — это шум. Перед
// сборкой документа он выставляется одной строкой, после — не важен.
let _шрифт = { шапкаИмя: 'Times New Roman', шапкаПт: 12, телоИмя: 'Times New Roman', телоПт: 12 };

/** Один абзац. Текст уже экранирован вызывающим. */
const _п = (xmlТекста, { жирный = false, размерПт = null, поЦентру = false, поПравому = false, шапка = false } = {}) => {
  const имя = шапка ? _шрифт.шапкаИмя : _шрифт.телоИмя;
  const пт = размерПт != null ? размерПт : (шапка ? _шрифт.шапкаПт : _шрифт.телоПт);
  const rPr = `<w:rPr><w:rFonts w:ascii="${имя}" w:hAnsi="${имя}" w:cs="${имя}"/>${жирный ? '<w:b/>' : ''}<w:sz w:val="${Math.round(пт * 2)}"/></w:rPr>`;
  const pPr = `<w:pPr>${поЦентру ? '<w:jc w:val="center"/>' : поПравому ? '<w:jc w:val="right"/>' : ''}</w:pPr>`;
  return `<w:p>${pPr}<w:r>${rPr}<w:t xml:space="preserve">${xmlТекста}</w:t></w:r></w:p>`;
};

/** Многострочный текст → абзацы (пустые строки сохраняются). */
const _многострочно = (текст, нстр) =>
  String(текст ?? '').split('\n').map((с) => _п(экр(с), нстр)).join('');

const _ячейка = (текст, жирный = false) =>
  `<w:tc><w:tcPr><w:tcBorders><w:top w:val="single" w:sz="4" w:color="999999"/><w:bottom w:val="single" w:sz="4" w:color="999999"/><w:left w:val="single" w:sz="4" w:color="999999"/><w:right w:val="single" w:sz="4" w:color="999999"/></w:tcBorders></w:tcPr>${_п(экр(текст), { жирный, размерПт: 11 })}</w:tc>`;

/** Таблица измерений: [{инструмент, значение}] → w:tbl. */
export function таблицаИзмеренийXml(измерения) {
  if (!измерения?.length) return '';
  const ряды = измерения.map((и) =>
    `<w:tr>${_ячейка(и.инструмент)}${_ячейка(и.значение)}</w:tr>`).join('');
  return `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/></w:tblPr>`
    + `<w:tr>${_ячейка('Инструмент', true)}${_ячейка('Значение', true)}</w:tr>${ряды}</w:tbl><w:p/>`;
}

/**
 * word/document.xml из данных бланка (см. собратьДанныеОтчёта).
 * @param {{шаблонИмя:string, physician:string, findings:string, impression:string,
 *          patientName:string, studyDate:string, modality:string,
 *          измерения?:{инструмент:string, значение:string}[]}} д
 */
/**
 * [11.1.30] Логотип КАРТИНКОЙ в DOCX. Раньше он был только в HTML-бланке —
 * в Word шапка ехала без герба. Картинка в OOXML это: файл в media,
 * связь в document.xml.rels, тип в [Content_Types] и абзац с w:drawing;
 * размеры — в EMU (914400 на дюйм), высота считается по пропорции.
 */
function _рисунокXml(ширинаМм, пропорция, поЦентру) {
  const emu = Math.round(ширинаМм * 36000);            // 1 мм = 36000 EMU
  const emuH = Math.round(emu * (пропорция || 1));
  return `<w:p><w:pPr>${поЦентру ? '<w:jc w:val="center"/>' : ''}</w:pPr><w:r><w:drawing>`
    + `<wp:inline distT="0" distB="0" distL="0" distR="0" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing">`
    + `<wp:extent cx="${emu}" cy="${emuH}"/><wp:docPr id="1" name="Логотип"/>`
    + `<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">`
    + `<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">`
    + `<pic:nvPicPr><pic:cNvPr id="1" name="Логотип"/><pic:cNvPicPr/></pic:nvPicPr>`
    + `<pic:blipFill><a:blip r:embed="rIdLogo" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>`
    + `<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${emu}" cy="${emuH}"/></a:xfrm>`
    + `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic>`
    + `</a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`;
}

/** data:image/... → {байты, расширение, тип} либо null. */
export function разобратьЛоготип(dataUrl) {
  const м = /^data:image\/(png|jpeg|jpg|gif);base64,([A-Za-z0-9+/=]+)$/.exec(String(dataUrl || '').trim());
  if (!м) return null;
  const расш = м[1] === 'jpg' ? 'jpeg' : м[1];
  const дв = typeof atob === 'function' ? atob(м[2]) : Buffer.from(м[2], 'base64').toString('binary');
  const байты = new Uint8Array(дв.length);
  for (let i = 0; i < дв.length; i++) байты[i] = дв.charCodeAt(i);
  return { байты, расширение: расш === 'jpeg' ? 'jpeg' : расш, тип: `image/${расш}` };
}

/** [11.1.30] Выставить шрифты документа перед сборкой. */
export function задатьШрифты(ш) {
  const имя = (з, п) => (typeof з === 'string' && з.trim() ? з.trim() : п);
  const пт = (з, п) => { const v = Number(з); return Number.isFinite(v) && v >= 8 && v <= 18 ? v : п; };
  _шрифт = {
    шапкаИмя: имя(ш?.шапкаИмя, 'Times New Roman'), шапкаПт: пт(ш?.шапкаПт, 12),
    телоИмя: имя(ш?.телоИмя, 'Times New Roman'), телоПт: пт(ш?.телоПт, 12),
  };
  return _шрифт;
}

export function собратьДокументXml(д = {}) {
  // [11.1.20] Разделы бланка приведены к живому заключению (образец
  // владельца): шапка учреждения с реквизитами, отделение, паспортная
  // часть, «Исследование проведено в ИП», клинические данные, описание,
  // измерения, сравнение с предыдущим, ЗАКЛЮЧЕНИЕ, рекомендации, подпись с
  // должностью. Пустые разделы не печатаются: прочерки там, где врачу
  // нечего сказать, только засоряют лист. Данные — ровно те же, что у
  // HTML-бланка (собратьДанныеОтчёта), поэтому формы не разъезжаются.
  const раздел = (заг, текст) => (String(текст || '').trim()
    ? _п(экр(заг), { жирный: true, размерПт: 13 }) + _многострочно(текст)
    : '');
  const строка = (подпись, значение) => {
    // [11.1.20] Абзац из ДВУХ прогонов: подпись жирная, значение обычное.
    // Прежний вариант дописывал закрывающие теги внутрь _п — получался
    // невалидный XML (Word такой файл не открыл бы вовсе), и поймала это
    // сверка настоящим XML-разборщиком, а не глазами.
    if (!String(значение || '').trim()) return '';
    const шрифт = (жирный) => `<w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>${жирный ? '<w:b/>' : ''}<w:sz w:val="22"/></w:rPr>`;
    return `<w:p><w:r>${шрифт(true)}<w:t xml:space="preserve">${экр(подпись)} </w:t></w:r>`
      + `<w:r>${шрифт(false)}<w:t xml:space="preserve">${экр(значение)}</w:t></w:r></w:p>`;
  };

  const л = д.логотипВид || {};
  const логотипXml = (д.логотип && л.место !== 'нет' && разобратьЛоготип(д.логотип))
    ? _рисунокXml(Number(л.ширинаМм) || 22, Number(л.пропорция) || 1, л.место !== 'слева')
    : '';
  const шапка = логотипXml
    + (д.учреждение ? _многострочно(д.учреждение, { жирный: true, поЦентру: true, шапка: true }) : '')
    + (д.реквизиты ? _многострочно(д.реквизиты, { размерПт: 9, поЦентру: true, шапка: true }) : '')
    + (д.отделение ? _п('', { размерПт: 6 }) + _п(экр(д.отделение), { жирный: true, поЦентру: true, шапка: true }) : '')
    + _п('', { размерПт: 8 });

  const паспорт =
      строка('Ф.И.О. пациента:', д.patientName || '—')
    + строка('Дата рождения:', д.датаРождения)
    + строка('Дата исследования:', д.studyDate || '—')
    + строка('№ истории болезни:', д.номерИстории)
    + строка('Аппарат:', д.аппарат)
    + _п('', { размерПт: 8 });

  // [11.1.30] Разделы печатаются по ГАЛОЧКАМ врача, а не по наличию текста:
  // пустая карта = все включены (старые профили), снятая галочка убирает
  // раздел, даже если текст в нём есть. Текст при этом жив — просто не
  // выводится. «ЗАКЛЮЧЕНИЕ» галочки не имеет: документ без него — не документ.
  const вкл = (к) => !д.разделы || !Object.keys(д.разделы).length || д.разделы[к] !== false;
  const тело =
      (вкл('ип') && д.последовательности ? _п(экр(`Исследование проведено в ИП: ${д.последовательности}.`)) : '')
    + (вкл('ип') && д.контраст ? _п(экр(`Контрастирование: ${д.контраст}.`)) : '')
    + (вкл('клинические') ? раздел('КЛИНИЧЕСКИЕ ДАННЫЕ', д.клинические) : '')
    + (вкл('описание') ? раздел('ОПИСАНИЕ', д.findings || '—') : '')
    + (вкл('измерения') && д.измерения?.length
        ? _п(экр(`ИЗМЕРЕНИЯ (${д.измерения.length})`), { жирный: true, размерПт: 13 }) + таблицаИзмеренийXml(д.измерения)
        : '')
    + (вкл('сравнение') ? раздел('СРАВНЕНИЕ С ПРЕДЫДУЩИМ ИССЛЕДОВАНИЕМ', д.сравнение) : '')
    + _п(экр('ЗАКЛЮЧЕНИЕ:'), { жирный: true, размерПт: 13 })
    + _многострочно(д.impression || '—', { жирный: true })
    + (вкл('рекомендации') ? раздел('РЕКОМЕНДАЦИИ', д.рекомендации) : '');

  const подпись =
      _п('', { размерПт: 14 })
    + (д.должность ? _п(экр(д.должность), { жирный: true, поПравому: true }) : '')
    + _п(экр(д.physician || '___________________'), { поПравому: true });

  // [11.1.35] Поля листа — из настроек бланка, а не зашитые. Раньше Word
  // получал 20 мм (и слева 25), печать — 18 мм, и поменять их врач не мог
  // вовсе (просьба владельца: 1,5 см с каждой стороны + настройка).
  // Word меряет в twips: 1 мм = 56,6929 twips; 15 мм ≈ 850.
  const тв = (мм, умолч) => { const v = Number(мм); return String(Math.round((Number.isFinite(v) && v >= 5 && v <= 40 ? v : умолч) * 56.6929)); };
  const п = д.поля || {};
  const pgMar = `<w:pgMar w:top="${тв(п.верхМм, 15)}" w:right="${тв(п.правоМм, 15)}" w:bottom="${тв(п.низМм, 15)}" w:left="${тв(п.левоМм, 15)}"/>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
<w:body>${шапка}${паспорт}${тело}${подпись}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/>${pgMar}</w:sectPr></w:body>
</w:document>`;
}

/** Все части архива DOCX. Чистая функция — для пинов. */
export function ЧАСТИ_DOCX(д = {}) {
  const лого = (д.логотип && (д.логотипВид?.место !== 'нет')) ? разобратьЛоготип(д.логотип) : null;
  const части = {
    '[Content_Types].xml':
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
${лого ? `<Default Extension="${лого.расширение}" ContentType="${лого.тип}"/>` : ''}
</Types>`,
    '_rels/.rels':
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    'word/document.xml': собратьДокументXml(д),
  };
  if (лого) {
    // Связь картинки: id должен совпасть с r:embed из _рисунокXml.
    части['word/_rels/document.xml.rels'] =
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rIdLogo" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/logo.${лого.расширение}"/>
</Relationships>`;
    части[`word/media/logo.${лого.расширение}`] = лого.байты;   // двоичная часть
  }
  return части;
}

/** Собрать и скачать .docx. @returns {Promise<number>} размер файла в байтах */
/**
 * [11.1.30] Собрать байты .docx БЕЗ скачивания. Понадобилось для
 * «открывать после сохранения»: в настольной сборке файл пишет оболочка
 * (диалог «Сохранить» + системное открытие), и ей нужны именно байты, а не
 * скачивание в «Загрузки», откуда путь неизвестен.
 */
export async function собратьDocxБайты(д = {}) {
  const { zip, strToU8 } = await import('fflate');
  задатьШрифты(д.шрифты);
  const части = ЧАСТИ_DOCX(д);
  const вход = {};
  // [11.1.30] Части бывают текстовые и ДВОИЧНЫЕ (логотип) — двоичные кладём
  // как есть: прогон через strToU8 испортил бы байты картинки.
  for (const [путь, содержимое] of Object.entries(части)) {
    вход[путь] = (содержимое instanceof Uint8Array) ? содержимое : strToU8(содержимое);
  }
  const байты = await new Promise((res, rej) =>
    zip(вход, { level: 6 }, (err, data) => (err ? rej(err) : res(data))));
  return байты;
}

export async function скачатьDocxОтчёт(д = {}, имяФайла = `report_${Date.now()}.docx`) {
  const байты = await собратьDocxБайты(д);
  const blob = new Blob([байты], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  downloadBlob(blob, имяФайла);
  return blob.size;
}

export default { ЧАСТИ_DOCX, собратьДокументXml, таблицаИзмеренийXml, скачатьDocxОтчёт, собратьDocxБайты, задатьШрифты, разобратьЛоготип };
